import { DocumentNode } from 'graphql/language';
import gql from 'graphql-tag';
import {
  ADDRESS_FRAGMENT_OPERATOR,
  SERVICEAREA_FRAGMENT_OPERATOR,
  DEBTOR_FRAGMENT_OPERATOR,
  BANKACCOUNT_FRAGMENT_OPERATOR,
} from '@/data/gql/operator/fragments';

export const LIST_USERS_FUZZY_OPERATOR: DocumentNode = gql`
  query listUsersFuzzyOperator(
    $where: userWhereInput
    $orderby: [userOrderByInput!]
    $first: Int
    $last: Int
    $before: userWhereUniqueInput
    $after: userWhereUniqueInput
  ) {
    users(where: $where, orderBy: $orderby, first: $first, last: $last, before: $before, after: $after) {
      id
      email
      title
      firstName
      lastName
      birthDate
      registrationDate
      termsAcceptedOn
      mobile1
      mobile2
      fax
      phone
      legacy
      roles
      deactivated
      deactivatedReason
      deactivatedOn
      deactivatedBy
      institutions {
        id
        verified
        name
        debtor {
          debtorNumber
        }
      }
      substitute {
        id
        verified
        debtor {
          debtorNumber
        }
      }
    }
  }
`;

export const LIST_USERS_PAGINATED_OPERATOR: DocumentNode = gql`
  query listUsersPaginatedOperator($where: userWhereInput, $orderBy: [userOrderByInput!], $skip: Int, $first: Int) {
    paginatedUsers(where: $where, orderBy: $orderBy, skip: $skip, first: $first) {
      totalCount
      users {
        id
        email
        title
        firstName
        lastName
        birthDate
        registrationDate
        termsAcceptedOn
        mobile1
        mobile2
        fax
        phone
        legacy
        roles
        deactivated
        deactivatedReason
        deactivatedOn
        deactivatedBy
        institutions {
          id
          verified
          name
          debtor {
            debtorNumber
          }
        }
        substitute {
          id
          verified
          debtor {
            debtorNumber
          }
        }
      }
    }
  }
`;

export const LIST_USERS_OPERATOR: DocumentNode = gql`
  query listUsersOperator(
    $userID: UUID
    $debtorNumber: Int
    $firstName: String
    $lastName: String
    $roles: [String!]
    $trainingConfirmed: Boolean
    $verified: Boolean
    $legacy: Boolean
    $cursor: UUID
    $take: Int
  ) {
    listCustomers(
      userID: $userID
      debtorNumber: $debtorNumber
      firstName: $firstName
      lastName: $lastName
      roles: $roles
      trainingConfirmed: $trainingConfirmed
      verified: $verified
      legacy: $legacy
      cursor: $cursor
      take: $take
    ) {
      id
      email
      title
      firstName
      lastName
      birthDate
      registrationDate
      termsAcceptedOn
      serviceFeeValue
      serviceFeeFrequency
      serviceFeeFirstChargeDate
      mobile1
      mobile2
      fax
      phone
      legacy
      roles
      institutions {
        id
        debtor {
          debtorNumber
        }
      }
      substitute {
        id
        debtor {
          debtorNumber
        }
      }
    }
  }
`;

export const LIST_USERS_FULL_OPERATOR: DocumentNode = gql`
  query listUsersFullOperator(
    $userID: UUID
    $debtorNumber: Int
    $firstName: String
    $lastName: String
    $roles: [String!]
    $trainingConfirmed: Boolean
    $verified: Boolean
    $legacy: Boolean
    $cursor: UUID
    $take: Int
  ) {
    listCustomers(
      userID: $userID
      debtorNumber: $debtorNumber
      firstName: $firstName
      lastName: $lastName
      roles: $roles
      trainingConfirmed: $trainingConfirmed
      verified: $verified
      legacy: $legacy
      cursor: $cursor
      take: $take
    ) {
      id
      email
      contactEmail
      invoicingEmail
      doctari_cognito_id
      speciality_area
      bag_handling_fee
      title
      gender
      memo
      firstName
      lastName
      birthDate
      registrationDate
      termsAcceptedOn
      serviceFeeValue
      serviceFeeFrequency
      serviceFeeFirstChargeDate
      mobile1
      mobile2
      fax
      phone
      legacy
      activeContract
      suspendedContractFee
      legacyInformation {
        bagHandlingFee
        bonusFee
        legacyDebtorNumber
        powerOfAttorney
        frameworkAgreement
        personalId
      }
      shippingAddress {
        id
        city
        street
        country
        houseNr
        zipCode
        state
        addressSupplement
      }
      roles
      deactivated
      deactivatedReason
      deactivatedOn
      deactivatedBy
      deletedAt
      institutions {
        id
        bsnr
        name
        type
        verified
        kvSeats
        address {
          ...addressFieldsOperator
        }
        debtor {
          ...debtorFieldsOperator
        }
        establishments {
          id
          bsnr
          name
          doctors {
            id
            name
            kvSeats
            lanr
          }
          billingAddress {
            ...addressFieldsOperator
          }
          serviceArea {
            ...serviceareaFieldsOperator
          }
        }
      }
      partnerLogisticsPreferences {
        id
        requiresDoctorsBagDelivery
        requiresFolderDelivery
        requiresCardReader
        cardReaderSerialNumbers
      }
      substitute {
        id
        taxNumber
        trainingConfirmed
        immunityProofVerified
        verified
        memo
        blackListedStates
        has_doctors_bag
        bankAccount {
          ...bankaccountFieldsOperator
        }
        debtor {
          ...debtorFieldsOperator
        }
        specializations {
          id
          verified
          specialization
          certificate {
            url
            uploadedOn
          }
        }
        address {
          ...addressFieldsOperator
        }
        license {
          url
          uploadedOn
        }
        liabilityInsurances {
          id
          validFrom
          validTo
          insuranceName
          insuranceNumber
          document {
            url
            uploadedOn
          }
        }
        measlesProofs {
          id
          validFrom
          validTo
          specification
          document {
            url
            uploadedOn
          }
        }
      }
    }
  }
  ${ADDRESS_FRAGMENT_OPERATOR}
  ${DEBTOR_FRAGMENT_OPERATOR}
  ${SERVICEAREA_FRAGMENT_OPERATOR}
  ${BANKACCOUNT_FRAGMENT_OPERATOR}
`;

export const FETCH_SUBSTITUTE_INSURANCES_OPERATOR: DocumentNode = gql`
  query listUsersOperator(
    $userID: UUID
    $debtorNumber: Int
    $firstName: String
    $lastName: String
    $roles: [String!]
    $trainingConfirmed: Boolean
    $verified: Boolean
    $legacy: Boolean
    $cursor: UUID
    $take: Int
  ) {
    listCustomers(
      userID: $userID
      debtorNumber: $debtorNumber
      firstName: $firstName
      lastName: $lastName
      roles: $roles
      trainingConfirmed: $trainingConfirmed
      verified: $verified
      legacy: $legacy
      cursor: $cursor
      take: $take
    ) {
      id
      substitute {
        id
        liabilityInsurances {
          id
          validFrom
          validTo
          insuranceName
          insuranceNumber
          document {
            url
            uploadedOn
          }
        }
      }
    }
  }
`;

export const FETCH_SUBSTITUTE_MEASLES_PROOF_OPERATOR: DocumentNode = gql`
  query listUsersOperator(
    $userID: UUID
    $debtorNumber: Int
    $firstName: String
    $lastName: String
    $roles: [String!]
    $trainingConfirmed: Boolean
    $verified: Boolean
    $legacy: Boolean
    $cursor: UUID
    $take: Int
  ) {
    listCustomers(
      userID: $userID
      debtorNumber: $debtorNumber
      firstName: $firstName
      lastName: $lastName
      roles: $roles
      trainingConfirmed: $trainingConfirmed
      verified: $verified
      legacy: $legacy
      cursor: $cursor
      take: $take
    ) {
      id
      substitute {
        id
        measlesProofs {
          id
          validFrom
          validTo
          specification
          document {
            url
            uploadedOn
          }
        }
      }
    }
  }
`;

export const FETCH_SUBSTITUTE_LICENSE_OPERATOR: DocumentNode = gql`
  query listUsersOperator(
    $userID: UUID
    $debtorNumber: Int
    $firstName: String
    $lastName: String
    $roles: [String!]
    $trainingConfirmed: Boolean
    $verified: Boolean
    $legacy: Boolean
    $cursor: UUID
    $take: Int
  ) {
    listCustomers(
      userID: $userID
      debtorNumber: $debtorNumber
      firstName: $firstName
      lastName: $lastName
      roles: $roles
      trainingConfirmed: $trainingConfirmed
      verified: $verified
      legacy: $legacy
      cursor: $cursor
      take: $take
    ) {
      id
      substitute {
        id
        license {
          url
          uploadedOn
        }
      }
    }
  }
`;

export const FETCH_SUBSTITUTE_SPECIALIZATIONS_OPERATOR: DocumentNode = gql`
  query listUsersOperator(
    $userID: UUID
    $debtorNumber: Int
    $firstName: String
    $lastName: String
    $roles: [String!]
    $trainingConfirmed: Boolean
    $verified: Boolean
    $legacy: Boolean
    $cursor: UUID
    $take: Int
  ) {
    listCustomers(
      userID: $userID
      debtorNumber: $debtorNumber
      firstName: $firstName
      lastName: $lastName
      roles: $roles
      trainingConfirmed: $trainingConfirmed
      verified: $verified
      legacy: $legacy
      cursor: $cursor
      take: $take
    ) {
      id
      substitute {
        id
        specializations {
          id
          specialization
          verified
          certificate {
            url
            uploadedOn
          }
        }
      }
    }
  }
`;

export const FETCH_CUSTOMER_LOGISTICS_OPERATOR: DocumentNode = gql`
  query listUsersOperator(
    $userID: UUID
    $debtorNumber: Int
    $firstName: String
    $lastName: String
    $roles: [String!]
    $trainingConfirmed: Boolean
    $verified: Boolean
    $legacy: Boolean
    $cursor: UUID
    $take: Int
  ) {
    listCustomers(
      userID: $userID
      debtorNumber: $debtorNumber
      firstName: $firstName
      lastName: $lastName
      roles: $roles
      trainingConfirmed: $trainingConfirmed
      verified: $verified
      legacy: $legacy
      cursor: $cursor
      take: $take
    ) {
      id
      partnerLogisticsPreferences {
        id
        requiresDoctorsBagDelivery
        requiresFolderDelivery
        requiresCardReader
        cardReaderSerialNumbers
      }
    }
  }
`;

export const OPERATOR_LOAD_SERVICE_AREA: DocumentNode = gql`
  query listServiceAreaLocations($serviceAreaID: UUID) {
    listServiceAreaLocations(serviceAreaID: $serviceAreaID) {
      id
      name
      validFrom
      validTo
      state
      mobileServices {
        id
        legacyID
        name
        shorthandName
        coordinates
        comment
        confidentialComment
      }
      officeServices {
        id
        legacyID
        name
        shorthandName
        comment
        confidentialComment
        address {
          id
          city
          street
          country
          houseNr
          zipCode
          state
        }
      }
    }
  }
`;

export const OPERATOR_FETCH_ACCOUNTING_MOBILE: DocumentNode = gql`
  query getAccountingServicesMobile(
    $where: mobile_serviceWhereInput
    $orderby: [mobile_serviceOrderByInput!]
    $first: Int
    $last: Int
    $before: mobile_serviceWhereUniqueInput
    $after: mobile_serviceWhereUniqueInput
  ) {
    mobileServices(where: $where, orderBy: $orderby, first: $first, last: $last, before: $before, after: $after) {
      serviceArea {
        name
        id
      }
      details {
        partnerConfirmedContractOn
        substituteConfirmedContractOn
        id
        service_tier
        applicationsCount
        discount
        allinclusive
        priority
        instantAssign
        legacy
        legacy_bonus_fee
        kvShareRateMobile
        kvShareRateOffice
        createdOn
        start
        end
        kind
        concluded
        establishmentDoctor {
          id
          name
          kvSeats
          lanr
        }
        regularFeeRange {
          min
          max
          current
        }
        serviceArea {
          name
          state
        }
        establishment {
          name
          billingAddress {
            city
            street
            country
            houseNr
            zipCode
            state
          }
          ownedBy {
            name
            debtor {
              debtorNumber
            }
          }
        }
        substitute {
          offer
          details {
            debtor {
              debtorNumber
              billingAddress {
                city
                street
                country
                houseNr
                zipCode
                state
              }
            }
            user {
              id
              email
              title
              firstName
              lastName
              birthDate
              mobile1
              mobile2
              fax
              phone
            }
            address {
              city
              street
              country
              houseNr
              zipCode
              state
            }
          }
        }
      }
    }
  }
`;

export const OPERATOR_FETCH_ACCOUNTING_OFFICE: DocumentNode = gql`
  query getAccountingServicesOffice(
    $where: office_serviceWhereInput
    $orderby: [office_serviceOrderByInput!]
    $first: Int
    $last: Int
    $before: office_serviceWhereUniqueInput
    $after: office_serviceWhereUniqueInput
  ) {
    officeServices(where: $where, orderBy: $orderby, first: $first, last: $last, before: $before, after: $after) {
      serviceArea {
        name
        id
      }
      details {
        id
        partnerConfirmedContractOn
        substituteConfirmedContractOn
        service_tier
        applicationsCount
        discount
        allinclusive
        priority
        instantAssign
        legacy
        legacy_bonus_fee
        kvShareRateMobile
        kvShareRateOffice
        createdOn
        start
        end
        kind
        concluded
        establishmentDoctor {
          id
          name
          kvSeats
          lanr
        }
        regularFeeRange {
          min
          max
          current
        }
        serviceArea {
          name
          state
        }
        establishment {
          name
          billingAddress {
            city
            street
            country
            houseNr
            zipCode
            state
          }
          ownedBy {
            name
            debtor {
              debtorNumber
            }
          }
        }
        substitute {
          offer
          details {
            debtor {
              debtorNumber
              billingAddress {
                city
                street
                country
                houseNr
                zipCode
                state
              }
            }
            user {
              id
              email
              title
              firstName
              lastName
              birthDate
              mobile1
              mobile2
              fax
              phone
            }
            address {
              city
              street
              country
              houseNr
              zipCode
              state
            }
          }
        }
      }
    }
  }
`;

export const GET_SUBSITUTE_BLACKLIST: DocumentNode = gql`
  query getOperatorBlacklistedStates($substituteID: UUID!) {
    getOperatorBlacklistedStates(substituteID: $substituteID)
  }
`;

export const LIST_EVENTS: DocumentNode = gql`
  query ListEvents($objectType: String, $objectId: UUID, $first: Int, $after: UUID) {
    listEvents(objectType: $objectType, objectId: $objectId, first: $first, after: $after) {
      edges {
        node {
          id
          eventType
          eventDatetime
          eventUser
          metadata
          previousState
          newState
          relatedObjectId
          relatedObjectType
          objectType
          objectId
          user {
            id
            firstName
            lastName
          }
          relatedObject {
            ... on SubstituteRelatedObject {
              user {
                id
                firstName
                lastName
              }
            }
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_FILE_FROM_S3: DocumentNode = gql`
  query getFileFromS3($filePath: String!) {
    getFileFromS3(filePath: $filePath)
  }
`;

export const PROVIDE_COGNITO_ACCESS: DocumentNode = gql`
  query provideCognitoAccess($token: String!) {
    provideCognitoAccess(token: $token)
  }
`;

export const LIST_SERVICES_PAGINATED_OPERATOR: DocumentNode = gql`
  query listServicesPaginated(
    $serviceID: UUID
    $start: DateTime
    $end: DateTime
    $serviceAreas: [String!]
    $minRegularFee: Float
    $minHolidayFee: Float
    $states: [String!]
    $types: [UserServiceType!]
    $assigned: Boolean
    $published: Boolean
    $concluded: Boolean
    $priority: Boolean
    $instantAssign: Boolean
    $cursor: UUID
    $take: Int
    $skip: Int
    $orderBy: String
    $sortOrder: String
    $state: String
    $orderNumber: String
    $allinclusive: Boolean
  ) {
    listServicesPaginated(
      serviceID: $serviceID
      start: $start
      end: $end
      serviceAreas: $serviceAreas
      minRegularFee: $minRegularFee
      minHolidayFee: $minHolidayFee
      states: $states
      types: $types
      assigned: $assigned
      published: $published
      concluded: $concluded
      priority: $priority
      instantAssign: $instantAssign
      cursor: $cursor
      take: $take
      skip: $skip
      orderBy: $orderBy
      sortOrder: $sortOrder
      state: $state
      orderNumber: $orderNumber
      allinclusive: $allinclusive
    ) {
      totalCount
      results {
        __typename
        ... on mobile_service {
          details {
            id
            createdOn
            legacy
            legacy_bonus_fee
            allinclusive
            start
            end
            orderNumber
            cancelled
            applicationsCount
            substitute {
              offer
              details {
                user {
                  firstName
                  lastName
                }
              }
            }
            establishmentDoctor {
              id
              name
            }
            regularFeeRange {
              min
              max
              current
            }
            serviceArea {
              name
              state
            }
            establishment {
              name
              ownedBy {
                name
                debtor {
                  debtorNumber
                }
              }
            }
          }
        }
        ... on office_service {
          details {
            id
            createdOn
            allinclusive
            start
            end
            orderNumber
            legacy
            legacy_bonus_fee
            cancelled
            applicationsCount
            substitute {
              offer
              details {
                user {
                  firstName
                  lastName
                }
              }
            }
            establishmentDoctor {
              id
              name
            }
            regularFeeRange {
              min
              max
              current
            }
            serviceArea {
              name
              state
            }
            establishment {
              name
              ownedBy {
                name
                debtor {
                  debtorNumber
                }
              }
            }
          }
        }
      }
    }
  }
`;
